import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';

import ControlledDateRangePicker from '../../components/BasicUI/ControlledInputs/ControlledDateRangePicker';
import ControlledGuestCountSelect
  from '../../components/BasicUI/ControlledInputs/ControlledGuestCountSelect/ControlledGuestCountSelect';
import { shouldDisableDateFn } from './BookingDatesForm.new.helpers';
import { diffTodayAndDate } from './BookingDatesForm.steps';

const formSx = { width: '100%' };

const isCheckoutBlocked = ({ startDate, endDate, fetchLineItemsInProgress, effectiveAnnualPassError }) =>
  !startDate || !endDate || fetchLineItemsInProgress || !!effectiveAnnualPassError;

/**
 * Booking fields of the panel: date range, guests, the annual-pass overlap warning and the
 * button that opens the checkout. Holds no state; the form lives in `useBookingForm`.
 *
 * @param {object} props
 * @param {import('react-hook-form').Control<any, any>} props.control
 * @param {Array} props.bookingDates - Current [start, end] of the form
 * @param {Array} props.guestOptions
 * @param {object} props.countLabels
 * @param {object} props.currentListing
 * @param {import('@vansite/ts-sharetribe-flex-sdk').TimeSlot[]} props.timeSlots
 * @param {number|null} [props.bookingLimit]
 * @param {number} [props.minimumNights]
 * @param {boolean} [props.fetchTimeSlotsInProgress]
 * @param {boolean} [props.fetchLineItemsInProgress]
 * @param {(monthOffset: number) => void} props.onFetchTimeSlots
 * @param {string|null} [props.annualPassDateOverlapError]
 * @param {React.ReactNode} [props.showAnnualPassError] - Count-based limit error, already shown by the panel
 * @param {React.ReactNode} [props.effectiveAnnualPassError] - Either error; blocks the checkout
 * @param {() => void} props.onOpenCheckout
 */
const BookingDatesFormComponent = ({
  control,
  bookingDates,
  guestOptions,
  countLabels,
  currentListing,
  timeSlots,
  bookingLimit = null,
  minimumNights = 1,
  fetchTimeSlotsInProgress = false,
  fetchLineItemsInProgress = false,
  onFetchTimeSlots,
  annualPassDateOverlapError,
  showAnnualPassError,
  effectiveAnnualPassError,
  onOpenCheckout,
}) => {
  const intl = useIntl();
  const maxGuestCount = currentListing.attributes.publicData.guestOptions?.maxGuestCount || 2;
  const [startDate, endDate] = bookingDates;

  // Disables unavailable/booked dates and dates outside the valid range.
  // MUI DateRangePicker passes (day, position) where position is "start" or "end"
  const shouldDisableDate = useMemo(
    () => shouldDisableDateFn(timeSlots, startDate, bookingLimit, minimumNights),
    [timeSlots, startDate, bookingLimit, minimumNights],
  );

  const datePickerProps = useMemo(
    () => ({
      loading: fetchTimeSlotsInProgress,
      shouldDisableDate,
      onMonthChange: (date) => onFetchTimeSlots(diffTodayAndDate(date)),
    }),
    [fetchTimeSlotsInProgress, shouldDisableDate, onFetchTimeSlots],
  );

  return (
    <Stack component="form" direction="column" spacing={2} sx={formSx}>
      <ControlledDateRangePicker
        control={control}
        datePickerProps={datePickerProps}
        name="bookingDates"
      />
      <ControlledGuestCountSelect
        control={control}
        countLabels={countLabels}
        guestOptions={guestOptions}
        label={intl.formatMessage({ id: 'BookingDatesForm.guestCountLabel' })}
        name="guestCount"
        totalMaximum={maxGuestCount}
      />
      <Typography color="text.secondary" variant="caption">
        {intl.formatMessage({ id: 'BookingDatesForm.maxGuestCountInfo' }, { count: maxGuestCount })}
      </Typography>
      {annualPassDateOverlapError && !showAnnualPassError ? (
        <Alert severity="warning">{annualPassDateOverlapError}</Alert>
      ) : null}
      <Button
        disabled={isCheckoutBlocked({
          startDate,
          endDate,
          fetchLineItemsInProgress,
          effectiveAnnualPassError,
        })}
        fullWidth
        loading={fetchLineItemsInProgress}
        onClick={onOpenCheckout}
        size="large"
        variant="contained"
      >
        {intl.formatMessage({ id: 'BookingDatesForm.completeBookingLabel' })}
      </Button>
    </Stack>
  );
};

const BookingDatesForm = memo(BookingDatesFormComponent);

export default BookingDatesForm;
